<template>
  <div class="app-container">
    <crud-table
      ref="seriesIndex"
      entity="ProductionPlanningSeries"
      :columns="columns"
      :allow-delete="false"
      :allow-edit="false"
      :allow-actions="true"
      :always-show-pagination="true"
      :on-row-dbl-click="editSeries"
      :on-open-context-menu="onOpenContextMenu"
      :on-context-menu-option-clicked="onContextMenuOptionClicked"
      :custom-actions="getCustomListActions()"
      :action-busy="actionBusy"
    />
    <select-printer-client-dialog
      :dialog-visible.sync="printDialogVisible"
      :on-select="printerClientSelected"
      :on-cancel="printerClientSelectionCancelled"
    />
  </div>
</template>

<script>
import CrudTable from '@/components/crud/CrudTable';
import SelectPrinterClientDialog from '@/pages/components/SelectPrinterClientDialog';
import i18n from '@/i18n';
import { PrintStatusOption } from '@/utils/constants';
import { ForceFileDownload } from '@/utils/general';

export default {
  components: {
    CrudTable,
    SelectPrinterClientDialog
  },
  data() {
    return {
      columns: [
        { field: 'weekNumber', label: 'series.weekNumber' },
        {
          field: 'domainName',
          label: 'series.domainName',
          minWidth: '115px',
          filter: {
            type: 'select',
            options: this.$store.getters.domains,
            valueField: 'id',
            labelField: 'name',
            filterName: 'domainID'
          }
        },
        {
          field: 'workshopName',
          label: 'series.workshopName',
          minWidth: '150px',
          filter: {
            type: 'select',
            options: this.$store.getters.workshops,
            valueField: 'id',
            labelField: 'name',
            filterName: 'workshopID'
          }
        },
        { field: 'seriesNumber', label: 'series.seriesNumber', minWidth: '110px' },
        { field: 'seriesDescription', label: 'series.seriesDescription', minWidth: '180px' },
        { field: 'storeAttributeName', label: 'domain.storeAttributes' },
        { field: 'amount', label: 'series.quantity', filter: { type: 'none' }, sortable: false },
        {
          field: 'amountUnit',
          label: 'series.unit',
          filter: { type: 'none' },
          formatter: this.formatUnit,
          sortable: false
        },
        {
          field: 'estimatedDeliveryDate',
          label: 'orderLine.estimatedDeliveryDate',
          type: 'date',
          sortable: false,
          filter: { type: 'none' }
        },
        {
          field: 'seriesStatus',
          label: 'series.seriesStatus',
          formatter: this.formatSeriesStatus,
          minWidth: '150px',
          filter: {
            type: 'select',
            options: this.$store.getters['option/loadedOptions']('seriesState'),
            valueField: 'code',
            labelField: 'description'
          }
        },
        { field: 'amountOfRings', label: 'series.amountOfRings', sortable: false, filter: { type: 'none' } },
        {
          field: 'amountOfWaves',
          label: 'series.amountOfWaves',
          sortable: false,
          filter: { type: 'none' },
          minWidth: '95px'
        },
        {
          field: 'amountOfEmbraces',
          label: 'series.amountOfEmbraces',
          sortable: false,
          filter: { type: 'none' },
          minWidth: '90px'
        },
        {
          field: 'amountOfOrders',
          label: 'series.amountOfOrders',
          sortable: false,
          filter: { type: 'none' },
          minWidth: '95px'
        },
        { field: 'amountOfOrderLines', label: 'series.amountOfOrderLines', sortable: false, filter: { type: 'none' } },
        { field: 'amountOfHangers', label: 'series.amountOfHangers', sortable: false, filter: { type: 'none' } },
        {
          field: 'Componenten',
          label: 'common.emptyString',
          type: 'link',
          filter: { type: 'none' },
          sortable: false,
          minWidth: '100px',
          href: '#/componentList'
        },
        {
          field: 'Open statuslist',
          label: 'common.emptyString',
          type: 'link',
          filter: { type: 'none' },
          sortable: false,
          minWidth: '100px'
        }
      ],
      dialogVisible: false,
      printDialogVisible: false,
      printedSeriesID: null,
      actionBusy: false
    };
  },
  methods: {
    downloadExcel() {
      this.loading = true;
      this.$store
        .dispatch('productionPlanningSeries/download', this.$refs.seriesIndex.listParams)
        .then(blob => {
          ForceFileDownload(blob, 'Series.xlsx');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatUnit(row, column, cellValue) {
      return cellValue ? this.$t('unit.' + cellValue) : '';
    },
    formatSeriesStatus(row, column, cellValue) {
      const option = this.$store.getters['option/loadedOptions']('seriesState').find(
        item => item.code === row.seriesStatus
      );
      return option && option.description;
    },
    editSeries(row) {
      const id = row.id;
      this.$router.push({ name: 'EditSeries', params: { id } });
    },
    getColumn(fieldName) {
      return this.columns.find(c => c.field === fieldName);
    },
    getCustomListActions() {
      return [
        {
          type: 'primary',
          icon: 'el-icon-printer',
          handleClick: this.handlePrintActionClicked,
          inRow: true
        },
        {
          type: 'primary',
          buttonTranslation: 'common.downloadExcel',
          handleClick: this.downloadExcel
        }
      ];
    },
    onOpenContextMenu(row, column, event) {
      const contextMenuOptions = [];
      contextMenuOptions.push({ name: i18n.t('series.printTickets'), type: 'printTickets' });
      return contextMenuOptions;
    },
    async onContextMenuOptionClicked(item, option) {
      if (option.type === 'printTickets') {
        await this.printTickets(item.id);
      }
    },
    async handlePrintActionClicked(row) {
      return this.printTickets(row.id);
    },
    doPromptReprintTicket(printedTickets, totalTickets) {
      return new Promise((resolve, reject) =>
        this.$store
          .dispatch('notify/userBlockableConfirm', {
            notificationCode: 'reprintTicket',
            cancelEqualsClose: false,
            hideDontShowAgainCheckbox: true,
            title: i18n.t('series.ticketsAlreadyPrintedTitle'),
            message: i18n.t('series.ticketsAlreadyPrintedMessage', { printedTickets, totalTickets }),
            cancelButtonText: i18n.t('series.ticketsAlreadyPrintedCancel'),
            confirmButtonText: i18n.t('series.ticketsAlreadyPrintedConfirm')
          })
          .then(response => {
            resolve('cancel');
          })
          .catch(
            // eslint-disable-next-line
             err => {
              resolve('confirm');
            }
          )
      );
    },
    async printTickets(seriesID) {
      this.printedSeriesID = seriesID;
      this.$store
        .dispatch('notify/confirm', { message: this.$i18n.t('series.confirmPrint') })
        .then(() => {
          this.printDialogVisible = true;
        })
        .catch(() => {
          console.log('print cancelled by user');
        });
    },
    async printerClientSelected(printerClientID) {
      this.printDialogVisible = false;
      return this.doPrintTickets(this.printedSeriesID, printerClientID);
    },
    printerClientSelectionCancelled() {
      this.printDialogVisible = false;
    },
    async doPrintTickets(seriesID, printerClientID) {
      const printCommand = {
        seriesID: seriesID,
        printerClientID: printerClientID,
        overrideAlreadyPrinted: false
      };
      const status = await this.$store.dispatch('productionPlanningSeries/getPrintStatus', { seriesID: seriesID });

      let alreadyPrintedCount = 0;
      let toBePrintedCount = 0;
      const printedStatusItem = status.printStatusItems.find(e => e.printStatusOption === PrintStatusOption.Printed);
      if (printedStatusItem) {
        alreadyPrintedCount += printedStatusItem.ticketCount;
      }
      const queuedStatusItem = status.printStatusItems.find(e => e.printStatusOption === PrintStatusOption.Queued);
      if (queuedStatusItem) {
        alreadyPrintedCount += queuedStatusItem.ticketCount;
      }
      const untriedStatusItem = status.printStatusItems.find(
        e => e.printStatusOption === PrintStatusOption.Unattempted
      );
      if (untriedStatusItem) {
        toBePrintedCount += untriedStatusItem.ticketCount;
      }
      const failedStatusItem = status.printStatusItems.find(e => e.printStatusOption === PrintStatusOption.Failed);
      if (failedStatusItem) {
        toBePrintedCount += failedStatusItem.ticketCount;
      }
      if (alreadyPrintedCount > 0) {
        const action = await this.doPromptReprintTicket(alreadyPrintedCount, status.totalTickets);
        printCommand.overrideAlreadyPrinted = action === 'confirm';
        toBePrintedCount += printCommand.overrideAlreadyPrinted ? alreadyPrintedCount : 0;
      }
      this.$store.dispatch('notify/success', {
        message: this.$i18n.t('series.seriesPrintStart', { toBePrintedCount })
      });
      this.actionBusy = true;
      this.$store
        .dispatch('productionPlanningSeries/printTickets', printCommand)
        .then(result => {
          const printDestination =
            this.$store.getters['printerClient/getCurrentPrinterClientName'] ||
            this.$i18n.t('common.plannedWorkshopPrintClient');
          this.actionBusy = false;
          this.$store.dispatch('notify/success', {
            message: this.$i18n.t('series.seriesPrintSuccess', { printDestination })
          });
        })
        .catch(error => {
          console.error(error);
          this.actionBusy = false;
          this.$store.dispatch('notify/error', { message: this.$i18n.t('series.seriesPrintError') });
        });
    }
  }
};
</script>
